import * as React                     from 'react';
import Box                            from '@mui/material/Box';
import { APIData }                    from 'types/cms/frontendTypes';
import { DataGridPro, 
         DataGridProProps, 
         GRID_CHECKBOX_SELECTION_COL_DEF, 
         GridActionsCellItem, 
         GridCellParams, 
         GridColDef, 
         GridHeaderFilterCellProps, 
         GridRenderCellParams, 
         GridRowId, 
         useGridApiRef,
       }                              from '@mui/x-data-grid-pro';
import { Badge,
         Snackbar,
         Alert, 
         Tooltip,
         FormControl,
         InputLabel,
         Select,
         MenuItem,
         Checkbox,
         FormControlLabel,
         useTheme
       }                              from '@mui/material';
import { useGlobalConfig }            from 'context/GlobalContext';
import { useSelector, 
         useDispatch 
       }                              from 'react-redux';
import { useEffect, 
         useMemo 
       }                              from 'react';
import { columnDefinition 
       }                              from './RecoTableColumns';
import SaveIcon                       from '@mui/icons-material/Save';
import RestoreIcon                    from '@mui/icons-material/Restore';
import PublishIcon                    from '@mui/icons-material/Publish';
import PreviewIcon                    from '@mui/icons-material/Preview';
import ForumIcon                      from '@mui/icons-material/Forum';
import FileDownloadOffIcon            from '@mui/icons-material/FileDownloadOff';
import DeleteIcon                     from '@mui/icons-material/Delete';
import TuneIcon                       from '@mui/icons-material/Tune';
import { BglCloseOutlinedSmallButton, 
         BglPrimaryOutlinedSmallButton, 
         BglSecondaryOutlinedSmallButton 
       }                              from 'components/button/BglButtons';
import { selectDraftRecos,
         selectPublishedRecos,
         selectModifiedRecos,
         selectUnsavedRows,
         selectHasUnsavedRows,
         setUnsavedRowAction,
         discardUnsavedRowAction,
         publishRecosAction,
         updateModifiedRecosAction,
         discardAllDraftRecosAction,
         unpublishRecosAction,
         setFiltersAction,
         clearFiltersAction,
         setSelectedLocaleAction,
         setShowOnlyUnsavedAction,
         selectRecoRanking,
         selectReviewWorkflow,
         selectTableState,
         selectShowOnlyUnsaved
       }                              from 'store/dataReducers/recommendationsSlice';
import { RecoApis }                   from 'apis/recoAPI';
import { RecoUtils }                  from '../utils/RecoUtils';
import { ROUTE_TREE }                 from 'routes/AppRoutes';
import { getRecoTableStyles }         from './RecoTableStyles';
import { selectHomeProfile, 
         selectHomeProfileMetadata 
       }                              from 'store/dataReducers/homeProfileSlice';
import { selectApplianceOwnership, 
         selectAppliances, 
         selectCategories
       }                              from 'store/dataReducers/applianceProfileSlice';
import { CMS_DEFAULT_LOCALE, 
         selectCMSLocales,
         selectCMSSelectedPilot, 
         selectCMSSelectedPilotRecoScore 
       }                              from 'store/dataReducers/cmsPilotSlice';
import { CONTENT_EDIT_ACTION, 
         Localization, 
         RECO_EDIT_STATUS, 
         RECO_VERIFICATION_STATUS
       }                              from 'types/apis/recoInterfaces';
import { isEqual }                    from 'lodash';
import { MultiChoiceFilterComponent } from './CustomHeaderFilters';
import { valueToArrayFilterOperator } from './CustomFilterOperators';
import { selectRecoCommentsCount, 
       }                              from 'store/dataReducers/recoCommentsSlice';

export interface TableProps {}

export default function RecommendationsTable(props : TableProps) {
  
  const { rc } = useGlobalConfig()
  const theme = useTheme()
  const apiRef = useGridApiRef()
  const dispatch = useDispatch()

  const [isSaving, setIsSaving]             = React.useState(false)
  const [selectedRows, setSelectedRows]     = React.useState<any[]>([])
  const [snackbar, setSnackbar]             = React.useState<{
    open: boolean;
    message: string;
    severity: 'error' | 'success' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'error'
  });

  const selectedPilot         = useSelector(selectCMSSelectedPilot)!
  const locales               = useSelector(selectCMSLocales)
  const recoScore             = useSelector(selectCMSSelectedPilotRecoScore)
  const homeProfileMetadata   = useSelector(selectHomeProfileMetadata)!
  const homeProfile           = useSelector(selectHomeProfile)!
  const appliances            = useSelector(selectAppliances)
  const applianceCategories   = useSelector(selectCategories)
  const applianceOwnership    = useSelector(selectApplianceOwnership)
  const recoRanking           = useSelector(selectRecoRanking)
  
  const draftRecos            = useSelector(selectDraftRecos)
  const publishedRecos        = useSelector(selectPublishedRecos)
  const modifiedRecos         = useSelector(selectModifiedRecos)
  
  const unsavedRows           = useSelector(selectUnsavedRows)
  const hasUnsavedRows        = useSelector(selectHasUnsavedRows)
  const tableState            = useSelector(selectTableState)
  const showOnlyUnsaved       = useSelector(selectShowOnlyUnsaved)
  const reviewWorkflow        = useSelector(selectReviewWorkflow)!
  const commentCounts         = useSelector(selectRecoCommentsCount)

  const memoizedRows = useMemo(() => {

    const publishedRecosWithoutModifications = publishedRecos.filter(
      pubReco => modifiedRecos.find(modReco => modReco.documentId === pubReco.documentId) === undefined)
    
    const allRows = [...draftRecos, ...modifiedRecos, ...publishedRecosWithoutModifications]
    
    // Filter for unsaved rows only if toggle is active
    if (showOnlyUnsaved) {

      const rows = allRows.filter(row => unsavedRows[row.id] !== undefined)
      if (rows.length === 0) {
        dispatch(setShowOnlyUnsavedAction(false))
        return allRows
      }
      return rows
    }
    
    return allRows

  } ,[draftRecos, modifiedRecos, publishedRecos, showOnlyUnsaved, unsavedRows, dispatch])

  // Initial page visit
  useEffect(() => {
    const supportedLocales = selectedPilot.supportedLocales as string[];
    if (supportedLocales && supportedLocales.length > 0) {
      const preferred = supportedLocales[0];
      if (preferred !== tableState.selectedLocale) {
        dispatch(setSelectedLocaleAction(preferred));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {

    Object.values(unsavedRows).forEach((unsavedRow) => {
      const originalRow = memoizedRows.find(r => r.documentId === unsavedRow.documentId)
      if (originalRow) {
        apiRef.current?.updateRows([{...originalRow, ...unsavedRow}]);
      }
    });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRef, memoizedRows])

  const navigateToRecoComments = React.useCallback(
    (event : any, reco : APIData<"api::recommendation.recommendation">) => {
      event.stopPropagation()
      rc.uiRouter.navigate(ROUTE_TREE.CMS.RECO.COMMENTS, {
        title : `Recommendation Comments`,
        id : reco.documentId
      })
  }, [rc])

  const previewReco = React.useCallback(
    async (event : any, reco : APIData<"api::recommendation.recommendation">) => {
      event.stopPropagation()
      rc.uiRouter.navigate(ROUTE_TREE.CMS.RECO.EDIT, {
        id : reco.documentId
      })
  }, [rc])


  const discardSelectedUnsavedRows = React.useCallback(() => {
    const selectedRowIds = selectedRows.map(row => row.id);
    
    // Filter unsaved rows to only include selected ones
    const selectedUnsavedRows = Object.values(unsavedRows).filter(row => 
      selectedRowIds.includes(row.id)
    );

    // Discard from table
    selectedUnsavedRows.forEach((row) => {
      const originalRow = memoizedRows.find(r => r.documentId === row.documentId)
      if (originalRow) {
        apiRef.current?.updateRows([originalRow]);
      }
    });
    
    // Discard from store - need to dispatch individual discard actions
    selectedUnsavedRows.forEach((row) => {
      dispatch(discardUnsavedRowAction({id: row.id, recoScore}));
    });

  }, [dispatch, unsavedRows, apiRef, memoizedRows, recoScore, selectedRows]);

  const discardUnsavedRow = React.useCallback((id : GridRowId) => {

    // Discard from table
    const unsavedRow = unsavedRows[id]
    const originalRow = memoizedRows.find(r => r.documentId === unsavedRow.documentId)
    if (originalRow) {
      apiRef.current?.updateRows([originalRow]);
    }
    // Discard from store
    dispatch(discardUnsavedRowAction({id, recoScore}));

  }, [dispatch, unsavedRows, apiRef, memoizedRows, recoScore]);

  const saveRecos = React.useCallback(async (recos : Partial<APIData<'api::recommendation.recommendation'>>[]) => {

    setIsSaving(true);

    const resp = await RecoApis.updateRecos(rc, selectedPilot.documentId, selectedPilot.pilotId, recos)    
    let succeeded = true

    if (resp.error || resp.data?.error) {
      let errorMsg = resp.error?.message || resp.data?.error
      if (errorMsg && typeof errorMsg === 'object') {
        errorMsg = JSON.stringify(errorMsg)
      }
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: 'error'
      })
      succeeded = false
    } else {
      const updatedRecos = resp.data!.recos
      apiRef.current?.updateRows(updatedRecos);
      dispatch(updateModifiedRecosAction({
        recos : updatedRecos,
        recoScore : recoScore
      }))
    }

    setIsSaving(false);
    return succeeded
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rc, apiRef, dispatch, recoScore])

  const saveSelectedUnsavedRows = React.useCallback(async () => {
    const selectedRowIds = selectedRows.map(row => row.id);
    
    // Filter unsaved rows to only include selected ones
    const selectedUnsavedRows = Object.values(unsavedRows).filter(row => 
      selectedRowIds.includes(row.id)
    );

    if (selectedUnsavedRows.length === 0) return;

    setIsSaving(true);

    const resp = await RecoApis.updateRecos(rc, selectedPilot.documentId, selectedPilot.pilotId, selectedUnsavedRows)
    let succeeded = true

    if (resp.error || resp.data?.error) {
      let errorMsg = resp.error?.message || resp.data?.error
      if (errorMsg && typeof errorMsg === 'object') {
        errorMsg = JSON.stringify(errorMsg)
      }
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: 'error'
      })
      succeeded = false
    } else {
      const updatedRecos = resp.data!.recos
      apiRef.current?.updateRows(updatedRecos);
      dispatch(updateModifiedRecosAction({
        recos : updatedRecos,
        recoScore : recoScore
      }))
    }

    setIsSaving(false);
    return succeeded
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rc, apiRef, dispatch, recoScore, unsavedRows, selectedRows])

  const discardAllDrafts = React.useCallback(async (docIds : string[]) => {

    setIsSaving(true);
    
    const resp = await RecoApis.discardDraftRecos(rc, selectedPilot.pilotId, docIds)

    if (resp.error) {
      setSnackbar({
        open: true,
        message: resp.error.message || 'Error discarding drafts',
        severity: 'error'
      })
    } else {
      dispatch(discardAllDraftRecosAction(resp.data))
    }

    setIsSaving(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [rc, dispatch])


  const publishSelectedRows = React.useCallback(async () => {
    const selectedRowsToPublish = selectedRows.filter(reco => {
      const workflowState = RecoUtils.getWorkflowStateForReco(reco.verificationStatus as any, reviewWorkflow, rc)
      return RecoUtils.isActionAllowed(CONTENT_EDIT_ACTION.PUBLISH, workflowState)
    })

    if (selectedRowsToPublish.length === 0) return;

    publishRecos(selectedRowsToPublish.map(reco => reco.documentId))

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRef, reviewWorkflow, rc, selectedRows])

  const publishRecos = React.useCallback(async (recoDocIds : string[]) => {

    setIsSaving(true);

    const resp = await RecoApis.publishRecos(rc, selectedPilot.pilotId, recoDocIds)

    if (resp.error) {
      setSnackbar({
        open: true,
        message: resp.error.message || 'Error publishing recommendations',
        severity: 'error'
      })
    } else {
      const updatedRecos = resp.data
      dispatch(publishRecosAction(updatedRecos))
    }

    setIsSaving(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [rc, dispatch])

  const unpublishRecos = React.useCallback(async (recoDocIds : string[]) => {

    setIsSaving(true);

    const resp = await RecoApis.unpublishRecos(rc, selectedPilot.pilotId, recoDocIds)

    if (resp.error) {
      setSnackbar({
        open: true,
        message: resp.error.message || 'Error unpublishing recommendations',
        severity: 'error'
      })
    } else {
      const draftRecos = resp.data
      dispatch(unpublishRecosAction(draftRecos))
    }

    setIsSaving(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [rc, dispatch])

  const processRowUpdate = React.useCallback<NonNullable<DataGridProProps['processRowUpdate']>>(
    async (newRow : any, oldRow : any) => {
      
      const memoRow = memoizedRows.find(r => r.documentId === newRow.documentId)!
      
      // Special handling for verificationStatus changes - make immediate API call
      if (newRow.verificationStatus !== oldRow.verificationStatus) {
        const succeeded = await saveRecos([{
          documentId : memoRow.documentId,
          recommendationId : memoRow.recommendationId,
          verificationStatus : newRow.verificationStatus
        }])
        return succeeded ? newRow : oldRow
      }
    
      const recoDiff = RecoUtils.getRecoDiff(newRow, memoRow) as any      

      if (Object.keys(recoDiff).length > 0) {
        recoDiff.id = memoRow.id;
        recoDiff.recommendationId = memoRow.recommendationId;
        recoDiff.documentId = memoRow.documentId;
        dispatch(setUnsavedRowAction({ id: memoRow.id, diff : recoDiff, recoScore : recoScore }));
      } else {
        dispatch(discardUnsavedRowAction({id: newRow.id, recoScore : recoScore}));
      }

      return newRow;

  }, [dispatch, memoizedRows, recoScore, saveRecos]);

  const onProcessRowUpdateError = React.useCallback<NonNullable<DataGridProProps['onProcessRowUpdateError']>>(
    (error : any) => {
      console.error(error)
    }, []
  )

  const getRowClassName = React.useCallback<NonNullable<DataGridProProps['getRowClassName']>>(
    ({ id }) => {

      const row = memoizedRows.find(r => r.id === id)
      if (row?.disabled) {
        return 'row--disabled';
      }

      const unsavedRow = unsavedRows[id];
      if (unsavedRow && Object.keys(unsavedRow).length > 0) {
        return 'row--edited';
      }
      return '';
  }, [memoizedRows, unsavedRows]);

  // Hightlight if value is modified b/w modified version and published version
  const getCellClassName = React.useCallback<NonNullable<DataGridProProps['getCellClassName']>>(
    (params : GridCellParams) => {

      const currentRow = memoizedRows.find(r => r.id === params.id);
      if (!currentRow || (currentRow as any).editStatus !== RECO_EDIT_STATUS.MODIFIED) {
        return '';
      }

      // Find the published version of this recommendation
      const publishedVersion = publishedRecos.find(r => r.recommendationId === (currentRow as any).recommendationId);
      if (!publishedVersion) {
        return '';
      }

      // Get the diff between current and published version
      const diff = RecoUtils.getRecoDiff(currentRow as any, publishedVersion as any) as any;

      const isLocalizationField = ['title', 'description', 'detail', 'ctaButtonText', 'ctaButtonLink', 'videoLink'].includes(params.field)
      const isCMSDefaultLocale = tableState.selectedLocale === CMS_DEFAULT_LOCALE

      if (!isCMSDefaultLocale) {
        if (isLocalizationField) {
          const currentLocalization = (currentRow as any).localizations.find((l : Localization) => l.locale === tableState.selectedLocale)
          const publishedLocalization = (publishedVersion as any).localizations.find((l : Localization) => l.locale === tableState.selectedLocale)
          
          const currValue = currentLocalization ? currentLocalization[params.field] : undefined
          const pubValue = publishedLocalization ? publishedLocalization[params.field] : undefined

          if (!isEqual(currValue, pubValue)) {
            return 'cell--edited'
          } else {
            return ''
          }
        }
      } 
      
      if (Object.keys(diff).includes(params.field)) {
        return 'cell--edited';
      }
    
      return '';
    }, [memoizedRows, publishedRecos, tableState.selectedLocale]);

  const isCellEditable = React.useCallback<NonNullable<DataGridProProps['isCellEditable']>>(
    (params : GridCellParams) => {

      if (params.field === 'verificationStatus') {
        return true
      }

      const workflowState = RecoUtils.getWorkflowStateForReco(params.row.verificationStatus, reviewWorkflow, rc)
      const isSaveAllowed = RecoUtils.isActionAllowed(CONTENT_EDIT_ACTION.SAVE_UPDATE, workflowState)
      
      if (!isSaveAllowed) {
        return false
      }

      if (params.field === 'disabled' && params.row.editStatus !== RECO_EDIT_STATUS.PUBLISHED) {
        return false
      }
      return true
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reviewWorkflow]
  )

  const { columnGroupingModel, columns } = useMemo(() => {
    return columnDefinition(rc, homeProfileMetadata, homeProfile, appliances, 
      applianceCategories, applianceOwnership, recoRanking, tableState.selectedLocale, theme)

  }, [rc, homeProfileMetadata, homeProfile, appliances, applianceCategories, 
      applianceOwnership, recoRanking, tableState.selectedLocale, theme])

  const memoColumns = useMemo<GridColDef[]>(() => {

    return [
      {
        field: 'actions',
        type: 'actions',
        width: 240,
        headerName: 'Actions',
        getActions : ({id, row}) => {
          
          const workflowState = RecoUtils.getWorkflowStateForReco(row.verificationStatus, reviewWorkflow, rc)
          const hasUnsavedChanges = unsavedRows[id] !== undefined
          
          // Check if each action is allowed by workflow
          const isDiscardAllowed = RecoUtils.isActionAllowed(CONTENT_EDIT_ACTION.DISCARD_UPDATE, workflowState)
          const isSaveAllowed = RecoUtils.isActionAllowed(CONTENT_EDIT_ACTION.SAVE_UPDATE, workflowState)
          const isPublishAllowed = RecoUtils.isActionAllowed(CONTENT_EDIT_ACTION.PUBLISH, workflowState)
          const isUnpublishAllowed = RecoUtils.isActionAllowed(CONTENT_EDIT_ACTION.UNPUBLISH, workflowState)
          const isDeleteAllowed = RecoUtils.isActionAllowed(CONTENT_EDIT_ACTION.DELETE, workflowState)
          
          return [
            <Tooltip title="Discard">
              <span>
                <GridActionsCellItem
                  icon={<RestoreIcon />}
                  label="Discard"
                  disabled={!isDiscardAllowed || !hasUnsavedChanges}
                  onClick={() => discardUnsavedRow(id)}
                />
              </span>
            </Tooltip>,
            <Tooltip title="Save">
              <span>
                <GridActionsCellItem
                  icon={<SaveIcon />}
                  label="Save"
                  disabled={!isSaveAllowed || !hasUnsavedChanges}
                  onClick={() => saveRecos([unsavedRows[id]])}
                />
              </span>
            </Tooltip>,
            <Tooltip title="Publish">
              <span>
                <GridActionsCellItem
                icon={<PublishIcon />}
                label="Publish"
                disabled={!isPublishAllowed || row.editStatus === RECO_EDIT_STATUS.PUBLISHED || hasUnsavedChanges}
                onClick={() => publishRecos([row.documentId])}
              />
              </span>
            </Tooltip>,
            <Tooltip title="Preview">
              <span>
                <GridActionsCellItem
                  icon={<PreviewIcon />}
                  label="Preview"
                  onClick={(event) => previewReco(event, row)}/>
              </span>
            </Tooltip>,
            <Tooltip title="Comments">
              <span>
                <GridActionsCellItem
                  icon={
                    <Badge badgeContent={(commentCounts as any)[row.documentId] || 0} color="warning">
                      <ForumIcon />
                    </Badge>
                  }
                  label="Comments"
                  onClick={(event) => navigateToRecoComments(event, row)}/>
              </span>
            </Tooltip>,
            <GridActionsCellItem
              icon={<FileDownloadOffIcon sx={{ transform: 'rotate(180deg)' }} />}
              label="Unpublish"
              showInMenu
              disabled={!isUnpublishAllowed || row.disabled || row.editStatus !== RECO_EDIT_STATUS.PUBLISHED || hasUnsavedChanges}
              onClick={() => unpublishRecos([row.documentId])}
              />,
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label={`${row.editStatus === RECO_EDIT_STATUS.DRAFT ? 'Delete draft' : 'Discard modification'}`} 
              showInMenu
              disabled={!isDeleteAllowed || row.editStatus === RECO_EDIT_STATUS.PUBLISHED || hasUnsavedChanges}
              onClick={() => discardAllDrafts([row.documentId])}
              />
          ]
        }
      },
      {
        field : 'verificationStatus',
        headerName : 'QA Status',
        width : 180,
        type : 'singleSelect',
        filterOperators : [valueToArrayFilterOperator()],
        renderHeaderFilter: (props : GridHeaderFilterCellProps) => {
          return <MultiChoiceFilterComponent 
                    {...props} 
                    options={Object.values(RECO_VERIFICATION_STATUS)} />
        },
        valueOptions : ({ id, row }) => {
          if (!id || !row) return []

          // Dont allow to change the QA status if there are unsaved changes or 
          // if the recommendation is published
          if (unsavedRows[id] !== undefined || row.editStatus === RECO_EDIT_STATUS.PUBLISHED) {
            return [row.verificationStatus]
          }
          
          const workflowState = RecoUtils.getWorkflowStateForReco(row.verificationStatus, reviewWorkflow, rc)
          const allowedTransitions = workflowState.nextQaStatus
          return [row.verificationStatus, ...allowedTransitions]
        },
        editable : true,
        renderCell : (params : GridRenderCellParams) => {
          return <div>{params.value}</div>
        }
      },
      
      ...columns
    ]
  }, [rc, commentCounts, unsavedRows, columns, reviewWorkflow, 
    previewReco, navigateToRecoComments, discardUnsavedRow, saveRecos, 
    publishRecos, unpublishRecos, discardAllDrafts])

  const handleFilterModelChange = React.useCallback((model: any) => {
    dispatch(setFiltersAction(model.items || []));
  }, [dispatch]);

  // Handle locale change
  const handleLocaleChange = (newLocale: string) => {
    dispatch(setSelectedLocaleAction(newLocale));
  };  

  const clearFilters = React.useCallback(() => {
    dispatch(clearFiltersAction());
    // Update the DataGridPro's filter model
    apiRef.current?.setFilterModel({ items: [] });
  }, [dispatch, apiRef]);

  const handleToggleUnsavedOnly = React.useCallback(() => {
    dispatch(setShowOnlyUnsavedAction(!showOnlyUnsaved));
    // Reset to first page when switching views
    apiRef.current?.setPage(0);
  }, [dispatch, showOnlyUnsaved, apiRef]);

  const handleSelectionChange = React.useCallback((rowSelectionModel: any, details: any) => {
    const selectedRowsData = Array.from(apiRef.current?.getSelectedRows()?.values() || []);
    setSelectedRows(selectedRowsData);
  }, [apiRef]);

  return (
    <>
    <Box sx={{ height: '100%', paddingRight: '20px' }}>

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>

        <div style={{ marginBottom: 8, display: 'flex', gap: 10 }}>

          <FormControlLabel
            control={
              <Checkbox
                checked={showOnlyUnsaved}
                onChange={handleToggleUnsavedOnly}
                disabled={!hasUnsavedRows || isSaving}
                size="small"
              />
            }
            label="Show Unsaved"
            sx={{ 
              marginLeft: 0,
              '& .MuiFormControlLabel-label': {
                fontSize: '14px',
                color: theme.palette.text.secondary
              }
            }}
          />
          
          {(() => {
            const selectedCount = selectedRows.length;
            const selectedUnsavedCount = selectedRows.filter(row => unsavedRows[row.id] !== undefined).length;
            const selectedPublishCandidates = selectedRows.filter(reco => {
              const workflowState = RecoUtils.getWorkflowStateForReco(reco.verificationStatus as any, reviewWorkflow, rc)
              return RecoUtils.isActionAllowed(CONTENT_EDIT_ACTION.PUBLISH, workflowState)
            });
            
            return (
              <>
                <BglCloseOutlinedSmallButton
                  disabled={selectedCount === 0 || selectedUnsavedCount === 0 || isSaving}
                  onClick={discardSelectedUnsavedRows}
                  startIcon={<RestoreIcon />}>
                  {`Discard selected (${selectedUnsavedCount})`}
                </BglCloseOutlinedSmallButton>
                
                <BglPrimaryOutlinedSmallButton
                  disabled={selectedCount === 0 || selectedUnsavedCount === 0 || isSaving}
                  onClick={saveSelectedUnsavedRows}
                  startIcon={<SaveIcon />}
                  loadingPosition="start">
                  {`Save selected (${selectedUnsavedCount})`}
                </BglPrimaryOutlinedSmallButton>
              
                <BglSecondaryOutlinedSmallButton
                  onClick={publishSelectedRows}
                  disabled={selectedCount === 0 || selectedPublishCandidates.length === 0}
                  startIcon={<PublishIcon />}>
                  {`Publish selected (${selectedPublishCandidates.length})`}
                </BglSecondaryOutlinedSmallButton>
              </>
            )
          })()}

          {tableState.filters.items.length > 0 && (
            <BglPrimaryOutlinedSmallButton
              onClick={clearFilters}
              startIcon={<TuneIcon />}>
              {`Clear filters (${tableState.filters.items.length})`}
            </BglPrimaryOutlinedSmallButton>
          )}

        </div>

        <div> 
          <BglPrimaryOutlinedSmallButton
            onClick={() => rc.uiRouter.navigate(ROUTE_TREE.CMS.RECO.SCORE)}>
            {"Modify Score"}
          </BglPrimaryOutlinedSmallButton>

          <FormControl size="small" sx={{ minWidth: 100, ml: 2 }}>
            <InputLabel sx={{ fontSize: '14px', color: theme.palette.text.secondary }}>Locale</InputLabel>
            <Select
              sx={{ 
                height: '36px', 
                fontSize: '12px',
                '.MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
                '.MuiSelect-select' : {
                  minHeight: 'unset',
                  color: theme.palette.text.secondary
                }
              }}
              value={tableState.selectedLocale}
              onChange={(e) => handleLocaleChange(e.target.value)}
              label="Locale"
            >
              {(selectedPilot.supportedLocales as string[]).map((locale) => (
                <MenuItem key={locale} value={locale} sx={{ color: theme.palette.text.secondary }}>
                  {locales.find(l => l.code === locale)?.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

        </div>

      </div>

      <DataGridPro
        checkboxSelection
        apiRef={apiRef}
        rowHeight={90}
        rows={memoizedRows}
        columns={memoColumns}
        columnGroupingModel={columnGroupingModel}
        pagination
        pageSizeOptions={[5, 10, 25, 100]}
        sx={getRecoTableStyles}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 25 },
          },
          pinnedColumns: {
            left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, 'recommendationId', 'category', 'title'],
          },
          filter: {
            filterModel: {
              items: tableState.filters.items
            }
          }
        }}
        disableRowSelectionOnClick
        disableColumnFilter
        headerFilters
        slotProps={{
          headerFilterCell: {
            showClearIcon: true,
          },
        }}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={onProcessRowUpdateError}
        loading={isSaving}
        getRowClassName={getRowClassName}
        getCellClassName={getCellClassName}
        isCellEditable={isCellEditable}
        onFilterModelChange={handleFilterModelChange}
        onRowSelectionModelChange={handleSelectionChange}
      />
      <br/>
    </Box>

    <Snackbar
      open={snackbar.open}
      autoHideDuration={5000}
      onClose={() => setSnackbar({ ...snackbar, open: false })}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert 
        onClose={() => setSnackbar({ ...snackbar, open: false })} 
        severity={snackbar.severity}
        sx={{ width: '100%' }}
      >
        {snackbar.message}
      </Alert>
    </Snackbar>
    </>
  );
}